import { useEffect, useState } from "react";
import { Phone, MessageSquare, Mail, CalendarClock, Bell, MapPin, StickyNote, MoreHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const ACTION_TYPES = [
  { v: "appel", l: "Appel", icon: Phone },
  { v: "sms", l: "SMS", icon: MessageSquare },
  { v: "email", l: "Email", icon: Mail },
  { v: "rdv", l: "Rendez-vous", icon: CalendarClock },
  { v: "relance", l: "Relance", icon: Bell },
  { v: "visite", l: "Visite", icon: MapPin },
  { v: "note", l: "Note", icon: StickyNote },
  { v: "autre", l: "Autre", icon: MoreHorizontal },
];
export const RESULTATS = [
  { v: "none", l: "—" },
  { v: "sans_reponse", l: "Sans réponse" },
  { v: "interesse", l: "Intéressé" },
  { v: "a_reflechir", l: "À réfléchir" },
  { v: "refus", l: "Refus" },
  { v: "valide", l: "Validé" },
  { v: "autre", l: "Autre" },
];
const RES_COLOR = {
  sans_reponse: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  interesse: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  a_reflechir: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  refus: "bg-red-500/10 text-red-400 border-red-500/20",
  valide: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  autre: "bg-slate-500/10 text-slate-300 border-slate-500/20",
};
const typeMeta = (t) => ACTION_TYPES.find((x) => x.v === t) || ACTION_TYPES[7];
const resLabel = (r) => (RESULTATS.find((x) => x.v === r) || {}).l || "";

export function ActionTimeline({ actions }) {
  if (!actions || actions.length === 0)
    return <p className="text-xs text-slate-500" data-testid="action-timeline-empty">Aucune action enregistrée pour l'instant.</p>;
  return (
    <div className="space-y-2" data-testid="action-timeline">
      {actions.map((a) => {
        const M = typeMeta(a.type);
        const Icon = M.icon;
        return (
          <div key={a.id} className="rounded-lg bg-[#111827] border border-[#1E293B] p-3" data-testid={`action-item-${a.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-sm font-semibold">
                <Icon className="h-3.5 w-3.5 text-amber-400" /> {M.l}
                {a.resultat_statut && (
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${RES_COLOR[a.resultat_statut] || RES_COLOR.autre}`}>{resLabel(a.resultat_statut)}</span>
                )}
              </span>
              <span className="text-[11px] text-slate-500 font-mono-metric">{(a.date || "").slice(0, 10)}</span>
            </div>
            {a.compte_rendu && <p className="mt-1 text-xs text-slate-300">{a.compte_rendu}</p>}
            {(a.prochaine_action || a.date_prochaine_relance) && (
              <div className="mt-1.5 flex items-center gap-2 text-[11px] text-amber-300/80">
                <Bell className="h-3 w-3" /> Prochaine : {a.prochaine_action || "relance"}{a.date_prochaine_relance ? ` · ${a.date_prochaine_relance.slice(0, 10)}` : ""}
              </div>
            )}
            {a.auteur && <div className="mt-1 text-[10px] text-slate-600">par {a.auteur}</div>}
          </div>
        );
      })}
    </div>
  );
}

const EMPTY = { type: "appel", resultat_statut: "none", compte_rendu: "", prochaine_action: "", date_prochaine_relance: "", devis_id: "none" };

export default function ActionLogDialog({ open, onOpenChange, context, dossiers, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;

    if (context?.preset_type === "relance") {
      const d = new Date();
      d.setDate(d.getDate() + 15);
      const dateJ15 = d.toISOString().slice(0, 10);

      setForm({
        ...EMPTY,
        type: "relance",
        prochaine_action: "Relancer le client",
        date_prochaine_relance: dateJ15,
      });
    } else {
      setForm({ ...EMPTY, type: context?.preset_type || "appel" });
    }
  }, [open, context]);

  const submit = async () => {
    if (!form.compte_rendu.trim() && form.type === "note") return toast.error("Compte rendu requis pour une note");
    setSaving(true);
    try {
      await api.post("/actions", {
        client_id: context?.client_id || null,
        project_id: context?.project_id || null,
        devis_id: form.devis_id !== "none" ? form.devis_id : (context?.devis_id || null),
        type: form.type,
        resultat_statut: form.resultat_statut === "none" ? "" : form.resultat_statut,
        compte_rendu: form.compte_rendu,
        prochaine_action: form.prochaine_action,
        date_prochaine_relance: form.date_prochaine_relance,
        reco_categorie: context?.reco_categorie || "",
        reco_action_code: context?.reco_action_code || "",
      });
      toast.success("Action enregistrée");
      window.dispatchEvent(new CustomEvent("crm:data-changed"));
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100 max-w-lg" data-testid="action-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Bell className="h-4 w-4 text-amber-400" /> Enregistrer une action réalisée</DialogTitle>
          <DialogDescription className="sr-only">Consigner une action commerciale et planifier la prochaine relance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {context?.client_nom && <div className="text-sm text-slate-400">Client : <span className="text-slate-100 font-semibold">{context.client_nom}</span></div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Type d'action</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="action-type" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                  {ACTION_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Résultat</Label>
              <Select value={form.resultat_statut} onValueChange={(v) => set("resultat_statut", v)}>
                <SelectTrigger data-testid="action-resultat" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                  {RESULTATS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {dossiers && dossiers.length > 0 && (
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Dossier concerné (optionnel)</Label>
              <Select value={form.devis_id} onValueChange={(v) => set("devis_id", v)}>
                <SelectTrigger data-testid="action-devis" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                  <SelectItem value="none">—</SelectItem>
                  {dossiers.map((d) => <SelectItem key={d.id} value={d.id}>{(d.famille || d.titre || "Dossier")} · {d.statut}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">Compte rendu</Label>
            <Textarea data-testid="action-compte-rendu" value={form.compte_rendu} onChange={(e) => set("compte_rendu", e.target.value)} className="bg-[#0B0F17] border-[#1E293B] min-h-[70px]" placeholder="Ex. Relancé le client, devis envoyé, réponse obtenue…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Prochaine action</Label>
              <Input data-testid="action-prochaine" value={form.prochaine_action} onChange={(e) => set("prochaine_action", e.target.value)} className="bg-[#0B0F17] border-[#1E293B]" placeholder="Ex. Rappeler, envoyer devis…" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Date prochaine relance</Label>
              <Input data-testid="action-date-relance" type="date" value={form.date_prochaine_relance} onChange={(e) => set("date_prochaine_relance", e.target.value)} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Annuler</Button>
          <Button data-testid="action-save" onClick={submit} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
